import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom, useAtomValue } from 'jotai';
import { AxiosError } from 'axios';
import { Box } from '@mui/material';
import Skeleton from '@/components/@IntranetLibrary/Skeleton';
import { styled } from '@mui/material/styles';
import HistoryIcon from '@/public/icons/approval/history.svg';
import ChevronUpIcon from '@/public/icons/approval/chevron-up.svg';
import ChevronDownIcon from '@/public/icons/approval/chevron-down.svg';
import TriangleDownIcon from '@/public/icons/approval/triangle-down.svg';
import CloseCircleIcon from '@/public/icons/approval/close-circle.svg';
import Bars2Icon from '@/public/icons/approval/2bars.svg';
import { ModalRef } from '@/components/@IntranetLibrary/Modal';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import { MyInfoState } from '@/recoil/atom';
import useApprovalViewRelations from '@/hooks/approval/useApprovalViewRelations';
import useRelatedApprovalMutations from '@/hooks/approval/useRelatedApprovalMutations';
import useApprovalView from '@/hooks/approval/useApprovalView';
import { getApprovalDocUrl } from '@/utils/urls';
import { formatApprovalDate } from '@/utils/approval/formatApprovalDate';
import type {
  AddRelationDocument,
  RelatedApprovalItem,
  RelatedApprovalRelationType,
} from '@/types/approval';
import RelatedItemRow from '@/components/Approval/RelatedApproval/RelatedItemRow';
import RelatedHistoryModal from './RelatedHistoryModal';
import RelatedSearch from './RelatedSearch';
import {
  relatedDeletedAtom,
  relatedEditModeAtom,
  relatedSelectedAtom,
  type RelatedSelected,
} from './atoms';
import {
  RelatedEditButton,
  RelatedEmpty,
  RelatedFoot,
  RelatedHeader,
  RelatedHeaderCount,
  RelatedHeaderHistoryButton,
  RelatedHeaderLeft,
  RelatedHeaderRight,
  RelatedHeaderToggleButton,
  RelatedRoot,
  RelatedSaveLabel,
  RelatedSaveSpinner,
  RelatedTypeBlock,
  RelatedTypeLabel,
  ViewRowChrome,
} from './styles';

type RelatedApprovalSlotProps = {
  documentId?: string | null;
};

const TYPE_LABEL: Record<RelatedApprovalRelationType, string> = {
  parents: '상위 품의',
  children: '하위 품의',
  related: '관련 품의',
};

const TYPE_ICON_CONFIG: Record<
  RelatedApprovalRelationType,
  {
    Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    size: number;
    color: string;
    rotate?: number;
  }
> = {
  parents: { Icon: TriangleDownIcon, size: 8, color: 'var(--dk-danger, #f55060)', rotate: 180 },
  children: { Icon: TriangleDownIcon, size: 8, color: 'var(--dk-accent, #0066cc)' },
  related: { Icon: Bars2Icon, size: 11, color: 'var(--dk-text-secondary, #888d96)' },
};

const TYPE_ORDER: RelatedApprovalRelationType[] = [
  'parents',
  'children',
  'related',
];

const EMPTY_SELECTED: RelatedSelected = {
  parents: [],
  children: [],
  related: [],
};

const sortByAuthoredAtDesc = (items: RelatedApprovalItem[]) =>
  [...items].sort((a, b) => {
    const aHas = !!a.authoredAt;
    const bHas = !!b.authoredAt;
    if (!aHas && !bHas) return 0;
    if (!aHas) return 1;
    if (!bHas) return -1;
    return b.authoredAt.localeCompare(a.authoredAt);
  });

const RelatedApprovalSlot = ({ documentId }: RelatedApprovalSlotProps) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const snackbar = useSnackbar();
  const myInfo = useAtomValue(MyInfoState);

  const {
    parents,
    children,
    related,
    isLoading,
    mutate: mutateRelations,
  } = useApprovalViewRelations(documentId ?? null);
  const { data: approvalData } = useApprovalView(documentId ?? null);
  const { addRelations, deleteRelations } = useRelatedApprovalMutations();

  const count = parents.length + children.length + related.length;

  const [isOpen, setIsOpen] = useState(() => count > 0);
  const [editMode, setEditMode] = useAtom(relatedEditModeAtom);
  const [selected, setSelected] = useAtom(relatedSelectedAtom);
  const [deleted, setDeleted] = useAtom(relatedDeletedAtom);
  const [isSaving, setIsSaving] = useState(false);

  const historyModalRef = useRef<ModalRef>(null);

  useEffect(() => {
    return () => {
      setEditMode(false);
      setSelected(EMPTY_SELECTED);
      setDeleted([]);
    };
  }, [documentId, setEditMode, setSelected, setDeleted]);

  // 최초 로드 건수로 열림/닫힘 1회 결정 — 매 재검증마다 적용하면 사용자 토글·편집 중 상태가 덮임
  const openInitedDocIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (isLoading) return;
    const docKey = documentId ?? null;
    if (openInitedDocIdRef.current === docKey) return;
    openInitedDocIdRef.current = docKey;
    setIsOpen(count > 0);
  }, [documentId, isLoading, count]);

  const canEdit = useMemo(() => {
    if (!myInfo?.passportId || !approvalData) return false;
    if (approvalData.author?.passportId === myInfo.passportId) return true;
    return (
      approvalData.approvers?.some(
        (a: { passportId: string }) => a.passportId === myInfo.passportId,
      ) ?? false
    );
  }, [myInfo, approvalData]);

  const deletedSetByType = useMemo(() => {
    const m: Record<RelatedApprovalRelationType, Set<string>> = {
      parents: new Set(),
      children: new Set(),
      related: new Set(),
    };
    deleted.forEach((d) => m[d.relationType].add(d.relatedId));
    return m;
  }, [deleted]);

  const byType = useMemo(() => {
    const base: Record<RelatedApprovalRelationType, RelatedApprovalItem[]> = {
      parents: sortByAuthoredAtDesc(parents),
      children: sortByAuthoredAtDesc(children),
      related: sortByAuthoredAtDesc(related),
    };
    if (!editMode) return base;
    TYPE_ORDER.forEach((type) => {
      base[type] = base[type].filter(
        (item) => !deletedSetByType[type].has(item.docId),
      );
    });
    return base;
  }, [parents, children, related, editMode, deletedSetByType]);

  const handleEnterEditMode = useCallback(() => {
    if (!canEdit) return;
    setIsOpen(true);
    setEditMode(true);
  }, [canEdit, setEditMode]);

  const resetEditState = useCallback(() => {
    setEditMode(false);
    setSelected(EMPTY_SELECTED);
    setDeleted([]);
  }, [setEditMode, setSelected, setDeleted]);

  const handleCancelEdit = useCallback(() => {
    dialog.show({
      message: (
        <>
          {t('취소 시 편집 내용이 저장되지 않습니다.')}
          <br />
          {t('편집을 취소하시겠습니까?')}
        </>
      ),
      confirmButtonText: t('확인'),
      cancelButtonText: t('계속 편집'),
      onConfirm: resetEditState,
    });
  }, [dialog, t, resetEditState]);

  const hasChanges = useMemo(
    () =>
      deleted.length > 0 ||
      TYPE_ORDER.some((type) => selected[type].length > 0),
    [deleted, selected],
  );

  const handleSave = useCallback(async () => {
    if (!documentId) return;
    if (!hasChanges) {
      resetEditState();
      return;
    }
    setIsSaving(true);
    try {
      if (deleted.length > 0) {
        await deleteRelations(
          documentId,
          deleted.map((d) => d.relatedId),
        );
      }
      const documentsToAdd: AddRelationDocument[] = TYPE_ORDER.flatMap((type) =>
        selected[type].map((it) => ({ id: it.docId, relationType: type })),
      );
      if (documentsToAdd.length > 0) {
        await addRelations(documentId, documentsToAdd);
      }
      await mutateRelations();
      resetEditState();
      snackbar.showSuccess({
        message: t('변경 내용이 성공적으로 저장되었습니다.'),
      });
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        snackbar.showError({
          message: t('품의를 찾을 수 없어요. 검색어를 다시 확인해 주세요.'),
        });
      } else {
        snackbar.showError({
          message: t('품의 연결에 실패했습니다. 다시 시도해 주세요.'),
        });
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    documentId,
    hasChanges,
    deleted,
    selected,
    deleteRelations,
    addRelations,
    mutateRelations,
    resetEditState,
    snackbar,
    t,
  ]);

  const handleDeleteExisting = useCallback(
    (type: RelatedApprovalRelationType, docId: string) => {
      setDeleted((prev) => {
        if (
          prev.some((d) => d.relationType === type && d.relatedId === docId)
        ) {
          return prev;
        }
        return [...prev, { relationType: type, relatedId: docId }];
      });
    },
    [setDeleted],
  );

  const handleSelectFromSearch = useCallback(
    (type: RelatedApprovalRelationType, item: RelatedApprovalItem) => {
      setSelected((prev) => ({
        ...prev,
        [type]: [...prev[type], item],
      }));
    },
    [setSelected],
  );

  const handleRemoveSelected = useCallback(
    (type: RelatedApprovalRelationType, docId: string) => {
      setSelected((prev) => ({
        ...prev,
        [type]: prev[type].filter((it) => it.docId !== docId),
      }));
    },
    [setSelected],
  );

  const ignoreDocIdsAll = useMemo(() => {
    const set = new Set<string>();
    if (documentId) set.add(documentId);
    TYPE_ORDER.forEach((type) => {
      byType[type].forEach((it) => set.add(it.docId));
      selected[type].forEach((it) => set.add(it.docId));
    });
    return set;
  }, [documentId, byType, selected]);

  const ChevronIcon = isOpen ? ChevronUpIcon : ChevronDownIcon;

  return (
    <RelatedRoot style={isSaving ? { cursor: 'wait' } : undefined}>
      <RelatedHeader>
        <RelatedHeaderLeft>
          {isLoading ? (
            <Skeleton variant="text" width={100} height={20} />
          ) : (
            <>
              {t('연결된 품의')}{' '}
              <RelatedHeaderCount>{count}</RelatedHeaderCount>
            </>
          )}
        </RelatedHeaderLeft>
        <RelatedHeaderRight>
          <RelatedHeaderHistoryButton
            type="button"
            onClick={() => historyModalRef.current?.open()}
          >
            <HistoryIcon width={18} height={18} />
            {t('변경이력')}
          </RelatedHeaderHistoryButton>
          <RelatedHeaderToggleButton
            type="button"
            aria-label={isOpen ? t('접기') : t('펼치기')}
            aria-expanded={isOpen}
            onClick={() => setIsOpen((v) => !v)}
          >
            <ChevronIcon width={14} height={14} />
          </RelatedHeaderToggleButton>
        </RelatedHeaderRight>
      </RelatedHeader>
      {isOpen && (
        <>
          {!editMode && isLoading && count === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                padding: '8px 0',
              }}
              aria-hidden
            >
              {[0, 1].map((i) => (
                <Box
                  key={i}
                  sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
                >
                  <Skeleton variant="text" width="55%" height={18} />
                  <Skeleton variant="text" width="30%" height={14} />
                </Box>
              ))}
            </Box>
          ) : !editMode && count === 0 ? (
            <RelatedEmpty>{t('연결된 품의가 없습니다.')}</RelatedEmpty>
          ) : (
            TYPE_ORDER.map((type) => {
              const list = byType[type];
              if (!editMode && list.length === 0) return null;
              const {
                Icon: TypeIcon,
                size,
                color,
                rotate,
              } = TYPE_ICON_CONFIG[type];
              return (
                <RelatedTypeBlock key={type}>
                  <RelatedTypeLabel>
                    <TypeIcon
                      width={size}
                      height={size}
                      style={{
                        color,
                        transform: rotate ? `rotate(${rotate}deg)` : undefined,
                      }}
                      aria-hidden="true"
                    />
                    {t(TYPE_LABEL[type])}
                  </RelatedTypeLabel>
                  {list.map((item) => (
                    <RelatedItemRow
                      key={item.docId}
                      rootComponent={ViewRowChrome}
                      title={item.subject}
                      href={getApprovalDocUrl(item.docId)}
                      titleTone="view"
                      dividerTone="view"
                      status={item.status}
                      author={item.author?.name ?? ''}
                      date={
                        formatApprovalDate(item.authoredAt) ?? t('날짜 없음')
                      }
                      trailing={
                        editMode ? (
                          <RelatedDeleteButton
                            type="button"
                            aria-label={t('연결 해제')}
                            onClick={() =>
                              handleDeleteExisting(type, item.docId)
                            }
                          >
                            <CloseCircleIcon width={18} height={18} />
                          </RelatedDeleteButton>
                        ) : undefined
                      }
                    />
                  ))}
                  {editMode &&
                    selected[type].map((item) => (
                      <RelatedItemRow
                        key={`sel-${item.docId}`}
                        rootComponent={ViewRowChrome}
                        title={item.subject}
                        titleTone="plain"
                        dividerTone="view"
                        status={item.status}
                        author={item.author?.name ?? ''}
                        date={
                          formatApprovalDate(item.authoredAt) ?? t('날짜 없음')
                        }
                        trailing={
                          <RelatedDeleteButton
                            type="button"
                            aria-label={t('선택 해제')}
                            onClick={() =>
                              handleRemoveSelected(type, item.docId)
                            }
                          >
                            <CloseCircleIcon width={18} height={18} />
                          </RelatedDeleteButton>
                        }
                      />
                    ))}
                  {editMode && (
                    <RelatedSearch
                      type={type}
                      ignoreDocIds={ignoreDocIdsAll}
                      onSelect={(item) => handleSelectFromSearch(type, item)}
                    />
                  )}
                </RelatedTypeBlock>
              );
            })
          )}
          <RelatedFoot>
            {!editMode && canEdit && (
              <RelatedEditButton type="button" onClick={handleEnterEditMode}>
                {t('편집')}
              </RelatedEditButton>
            )}
            {editMode && (
              <>
                <BoxButton
                  variant="secondary"
                  size="small"
                  type="button"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  {t('취소')}
                </BoxButton>
                <BoxButton
                  variant="primary"
                  size="small"
                  type="button"
                  onClick={handleSave}
                  disabled={!hasChanges || isSaving}
                >
                  <RelatedSaveLabel>
                    <span
                      style={{ visibility: isSaving ? 'hidden' : 'visible' }}
                    >
                      {t('저장')}
                    </span>
                    {isSaving && (
                      <RelatedSaveSpinner>
                        <Spinner theme="white" size="16px" />
                      </RelatedSaveSpinner>
                    )}
                  </RelatedSaveLabel>
                </BoxButton>
              </>
            )}
          </RelatedFoot>
        </>
      )}
      <RelatedHistoryModal
        ref={historyModalRef}
        documentId={documentId ?? null}
      />
    </RelatedRoot>
  );
};

export default RelatedApprovalSlot;

const RelatedDeleteButton = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  margin-left: 4px;
  border: none;
  background: transparent;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;
